// @flow

import React from 'react';
import moment from 'moment';
import numeral from 'numeral';
import styled from '@emotion/styled';

import t from 'app/utils/translate';
import CurrencyIcon from 'app/components/commons/CurrencyIcon';
import StarSymbol from 'app/components/challenge/StarSymbol';
import Icon from 'app/components/commons/Icon';
import SvgButton from 'app/components/commons/SvgButton';
import { CountdownIcon } from 'app/components/commons/CountdownClock';
import ChallengeStatusPill from './ChallengeStatusPill';

type Props = {
  dark?: boolean,
  id?: number | string,
  entriesLabel?: string,
  favorite?: boolean,
  isLive?: boolean,
  name: string,
  numContests?: number,
  numEntries?: number,
  remainingSalary?: number,
  onToggleFavorite?: (id: number, favorite: boolean) => void,
  projectedScore?: number,
  sportIcon: string,
  startTime?: number | string,
  statusId?: number,
  statusLabel?: string
};

export const LineUpHeaderWrapper = styled.div(({ dark, theme }) => ({
  backgroundColor: dark ? theme.color.darkBackground : theme.color.basis3,
  color: theme.color.white,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0.8em',
  flexShrink: 0,
  textAlign: 'left',
  fontSize: theme.fontSize.small
}));

export const SportsIcon = styled(Icon)(({ theme }) => ({
  color: theme.color.white,
  // fontSize: 28,
  marginBottom: 4,
  height: 40,
  width: 40,
  paddingRight: '0.5em'
}));

const Name = styled.span(({ theme }) => ({
  fontSize: theme.fontSize.default
}));

const PillWrapper = styled.div(
  {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    paddingTop: '0.2em',
    '& > *:not(:last-child)': {
      marginRight: '0.8em'
    },
    textTransform: 'uppercase'
  },
  ({ theme }) => ({
    color: theme.color.basis5
  })
);

const Bold = styled.span(({ theme }) => ({
  fontWeight: theme.fontWeight.demiBold,
  color: theme.color.white
}));

const Middle = styled.div({
  flexGrow: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
});

const StarButton = styled(SvgButton)(({ favorite, theme }) =>
  favorite
    ? {
        fill: theme.color.info
      }
    : {
        fill: 'none',
        stroke: theme.color.info,
        strokeWidth: 3,
        strokeMiterlimit: 10
      }
);

const NameLine = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
});

function LineUpHeader({
  dark,
  id,
  entriesLabel,
  favorite,
  isLive,
  name,
  numContests,
  numEntries,
  remainingSalary,
  onToggleFavorite,
  projectedScore,
  sportIcon,
  startTime,
  statusId,
  statusLabel
}: Props) {
  return (
    <LineUpHeaderWrapper dark={dark}>
      <SportsIcon type={`icon-sport-${sportIcon}`} />
      <Middle>
        <NameLine>
          <Name>{name}</Name>
          {typeof onToggleFavorite === 'function' && id && (
            <StarButton
              type="icon-star"
              size={20}
              favorite={favorite}
              onClick={() => onToggleFavorite(id, !favorite)}
            />
          )}
        </NameLine>
        <PillWrapper>
          {!isLive && startTime && (
            <span>
              <CountdownIcon />
              {`  `}
              {moment(startTime).format('ddd h:mm a')}
            </span>
          )}
          {numContests && (
            <span>
              {t('Challenges')}: <Bold>{numContests}</Bold>
            </span>
          )}
          {Number.isInteger(numEntries) && (
            <span>
              {entriesLabel}: <Bold>{numEntries}</Bold>
            </span>
          )}
          {Number.isInteger(remainingSalary) && (
            <span>
              Rem. Salary:{' '}
              <Bold>
                <CurrencyIcon />
                {numeral(remainingSalary).format('0,0')}
              </Bold>
            </span>
          )}
          {projectedScore && (
            <span>
              Projection: <StarSymbol>{projectedScore}</StarSymbol>
            </span>
          )}
          {isLive && <ChallengeStatusPill statusId={statusId}>{statusLabel}</ChallengeStatusPill>}
        </PillWrapper>
      </Middle>
    </LineUpHeaderWrapper>
  );
}

LineUpHeader.defaultProps = {
  dark: false,
  entriesLabel: 'Entries',
  id: undefined,
  favorite: false,
  isLive: false,
  numEntries: undefined,
  onToggleFavorite: undefined,
  projectedScore: undefined,
  numContests: undefined,
  remainingSalary: undefined,
  startTime: undefined,
  statusId: undefined,
  statusLabel: undefined
};

export default LineUpHeader;
